import { useState } from 'react'
import type { SxProps, Theme } from '@mui/material'

import { Button } from '@repo/ui'

import { PermissionGate } from 'components/access/PermissionGate'
import { useClearIntroData } from 'hooks/api'
import { ConfirmSettingModal } from '../modals/ConfirmSettingModal'

interface ClearSampleDataButtonProps {
  /**
   * What clearing costs ON THIS SURFACE, in the user's terms (§43). Required, and
   * deliberately NOT defaulted: the two call sites answer it differently because the
   * page the reader is standing on changes what they will see happen. Analytics has
   * to say the charts will drop; Home has no charts and would be stating a
   * consequence the reader cannot observe.
   *
   * A default would have made the wrong copy the path of least resistance, which is
   * how a `consequence` slot degrades into "are you sure?" one call site at a time.
   */
  consequence: React.ReactNode
  /**
   * Default `secondary`, matching the treatment the Analytics banner already shipped.
   * Exposed for a surface where a secondary button would out-shout its container.
   */
  variant?: 'secondary' | 'tertiary'
  sx?: SxProps<Theme>
}

/**
 * "Clear sample data" + its confirmation, as one unit (VRG-512).
 *
 * ── Why this is a component and not two copies of nine lines ────────────────────────
 * §36: one hand-roll is allowed, the second is a shared component. The Analytics
 * banner hand-rolled this flow; Home's sample-data card was about to hand-roll it
 * again, which would have put the same destructive action on two surfaces with two
 * independent implementations of its confirm, its loading state, and — the detail
 * that actually drifts — its FAILURE behaviour.
 *
 * That failure behaviour is the load-bearing bit and is the reason a shared component
 * beats a shared copy string: on a rejected clear the modal stays OPEN. Closing it
 * would look exactly like success, and the caller's own gate would then re-render the
 * button on the next settle, leaving the user to conclude the action is broken rather
 * than that it failed. One copy of that decision, not two.
 *
 * ── What it deliberately does NOT do: decide whether to render ──────────────────────
 * No `hasIntroData` check here. Both callers already branch on the intro-data state to
 * choose WHICH affordance to show, so a second gate inside this component would be a
 * second authority on the same question — and the two would disagree the first time
 * one of them was refined. The caller owns "should this be offered"; this owns "what
 * happens when it is pressed".
 *
 * ── The permission gate lives HERE, not at the call sites ───────────────────────────
 * `sample_data_manage` gates `DELETE /intro-data`, so a user without it can see the
 * samples and could press a button that 403s. The gate sits inside this component for
 * the same reason the confirm does: two call sites, one decision. Gating at each caller
 * would be an opt-in that the third call site forgets — §42's referrer prop, which 35
 * of 37 sites forgot, is exactly this shape.
 *
 * `mode="disable"`, NOT hide (§27/§46). This is an in-context action on a surface the
 * user can already see and whose subject — the sample records — is visibly in their
 * lists. Hiding it would leave them looking at data they cannot explain and cannot
 * remove, with nothing on screen saying who can. Disabled + a tooltip naming the
 * permission tells them what to ask for.
 *
 * Note the gate wraps the BUTTON, not this component's whole return: `PermissionGate`
 * clones its child with `disabled`, which a fragment containing a modal cannot accept.
 * The modal stays outside it and unreachable, since only the button opens it.
 *
 * ── `destructive="hover"` ───────────────────────────────────────────────────────────
 * §32: this trigger is PERSISTENT chrome (it sits on the page for as long as samples
 * exist), and it destroys records, so it is neutral at rest and reddens on hover and
 * focus. At rest it is pixel-identical to the plain secondary button the Analytics
 * banner shipped, so adopting the shared component changes that surface's resting
 * appearance not at all — it only adds the engagement cue that was missing.
 */
export const ClearSampleDataButton = ({
  consequence,
  variant = 'secondary',
  sx,
}: ClearSampleDataButtonProps) => {
  const { clearIntroDataAsync, isClearing } = useClearIntroData()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const handleConfirm = async () => {
    try {
      await clearIntroDataAsync()
      setConfirmOpen(false)
    } catch {
      // Left open on purpose. See the docblock: a modal that closes on failure is
      // indistinguishable from one that closed on success.
    }
  }

  return (
    <>
      <PermissionGate
        permission="sample_data_manage"
        tooltip="You need the “Manage sample data” permission to clear these samples. Ask an admin to grant it, or to clear them for you."
      >
        <Button
          variant={variant}
          destructive="hover"
          onClick={() => setConfirmOpen(true)}
          sx={sx}
        >
          Clear sample data
        </Button>
      </PermissionGate>

      <ConfirmSettingModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        loading={isClearing}
        // The records genuinely cease to exist, so the confirm is filled red (§32's
        // transient/committed tier) even though the trigger above stays quiet.
        destructive
        title="Clear your sample data?"
        consequence={consequence}
        // A verb, never "OK" (§43).
        confirmLabel="Clear sample data"
      />
    </>
  )
}
